import { useEffect, useSyncExternalStore } from "react";
import { api } from "../api/api";
import { useContextUserId } from "./useSupportContext";

/**
 * The organization's brand kit (Varumärke), cached per context user and shared by the brand page,
 * the sidebar and the design editor. setBrandKit() after a save updates every reader at once.
 */
const EMPTY = { website: "", colors: [], fonts: [], logos: [] };
const cache = new Map(); // contextUserId -> { kit, canManage, organization }
const inFlight = new Map();
const listeners = new Set();
let current = null;

function emit() {
  listeners.forEach((l) => l());
}

export function setBrandKit(contextUserId, patch) {
  const prev = cache.get(contextUserId) || { kit: EMPTY, canManage: false, organization: false };
  cache.set(contextUserId, { ...prev, ...patch });
  emit();
}

function load(contextUserId, force = false) {
  if (!contextUserId) return Promise.resolve();
  if (!force && (cache.has(contextUserId) || inFlight.has(contextUserId))) return inFlight.get(contextUserId) || Promise.resolve();
  const p = api
    .get(`/brand/${contextUserId}`)
    .then((r) => setBrandKit(contextUserId, { kit: r.data?.kit || EMPTY, canManage: !!r.data?.canManage, organization: !!r.data?.organization }))
    .catch(() => {
      if (!cache.has(contextUserId)) setBrandKit(contextUserId, { kit: EMPTY });
    })
    .finally(() => inFlight.delete(contextUserId));
  inFlight.set(contextUserId, p);
  return p;
}

function subscribe(l) {
  listeners.add(l);
  return () => listeners.delete(l);
}

export function useBrandKit() {
  const contextUserId = useContextUserId();
  current = contextUserId;
  const entry = useSyncExternalStore(subscribe, () => cache.get(contextUserId));
  useEffect(() => {
    load(contextUserId);
  }, [contextUserId]);
  return {
    contextUserId,
    kit: entry?.kit || EMPTY,
    loaded: !!entry,
    canManage: !!entry?.canManage,
    organization: !!entry?.organization,
    reload: () => load(contextUserId, true),
  };
}

/** react-color's own swatches, after the brand colors. */
const DEFAULT_PRESETS = [
  "#D0021B", "#F5A623", "#F8E71C", "#8B572A", "#7ED321", "#417505", "#BD10E0", "#9013FE",
  "#4A90E2", "#50E3C2", "#B8E986", "#000000", "#4A4A4A", "#9B9B9B", "#FFFFFF",
];

/** presetColors for every SketchPicker in the design editor: brand colors first. */
export function brandPresetColors() {
  const brand = (cache.get(current)?.kit?.colors || []).map((c) => ({ color: c.hex, title: c.name || c.hex }));
  const taken = new Set(brand.map((b) => b.color.toLowerCase()));
  return [...brand, ...DEFAULT_PRESETS.filter((c) => !taken.has(c.toLowerCase()))];
}

/** Brand font families, for the editor's font list. */
export function brandFontFamilies() {
  return (cache.get(current)?.kit?.fonts || []).map((f) => f.family);
}
